import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { NavLink, Outlet, useLocation } from 'react-router'
import { useEffect } from 'react'

import { cn } from '@/lib/cn'
import { useUiStore } from '@/stores/uiStore'
import { CommandPalette } from '@/app/CommandPalette'
import { useBreadcrumbLabels } from './breadcrumbLabels'
import { MOBILE_NAVIGATION } from './navigation'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import type { BreadcrumbItem } from './PageHeader'

/**
 * Udleder brødkrummer af stien.
 *
 * De stumper, der ikke kan læses — først og fremmest id'er — får deres navn
 * fra den side, der viser dem; se `breadcrumbLabels`. Indtil navnet er hentet,
 * står der en beskrivelse af hvad man er inde i frem for et råt id.
 */
const SEGMENT_LABELS: Record<string, string> = {
  app: 'BygOverblik',
  dashboard: 'Overblik',
  projects: 'Projekter',
  documents: 'Dokumenter',
  drawings: 'Tegninger',
  tenders: 'Udbud',
  tasks: 'Opgaver',
  quality: 'Checklister',
  communication: 'Kommunikation',
  sustainability: 'Bæredygtighed',
  operations: 'Drift og vedligehold',
  reports: 'Rapporter',
  settings: 'Indstillinger',
  profile: 'Profil',
  security: 'Sikkerhed',
  organization: 'Organisation',
  moduler: 'Moduler',
  members: 'Medlemmer',
  hjaelp: 'Hjælp',
  new: 'Nyt',
  platform: 'Platform',
  kunder: 'Kunder',
  brugere: 'Brugere',
}

/**
 * Hvad en id-stump kaldes, indtil siden har meldt det rigtige navn ind.
 *
 * Nøglen er stumpen foran: `/app/projects/019f…` bliver til "Projekt". Det er
 * et halvt sekund af brugerens liv, men et råt id er aldrig svaret.
 */
const ID_FALLBACKS: Record<string, string> = {
  reports: 'Rapport',
  projects: 'Projekt',
  documents: 'Dokument',
  drawings: 'Tegning',
  tasks: 'Opgave',
  tenders: 'Udbud',
  kunder: 'Kunde',
}

const LOOKS_LIKE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function useBreadcrumbs(): BreadcrumbItem[] {
  const { pathname } = useLocation()
  const labels = useBreadcrumbLabels()

  const segments = pathname.split('/').filter(Boolean)

  return segments.map((segment, index) => ({
    label:
      SEGMENT_LABELS[segment] ??
      labels[segment] ??
      (LOOKS_LIKE_ID.test(segment)
        ? (ID_FALLBACKS[segments[index - 1] ?? ''] ?? 'Detaljer')
        : segment),
    to: index < segments.length - 1 ? `/${segments.slice(0, index + 1).join('/')}` : undefined,
  }))
}

export function AppShell() {
  const collapsed = useUiStore((state) => state.sidebarCollapsed)
  const mobileNavOpen = useUiStore((state) => state.mobileNavOpen)
  const setMobileNavOpen = useUiStore((state) => state.setMobileNavOpen)
  const breadcrumbs = useBreadcrumbs()
  const { pathname } = useLocation()

  // Drawer'en må ikke blive stående åben, når brugeren har navigeret videre.
  useEffect(() => {
    setMobileNavOpen(false)
  }, [pathname, setMobileNavOpen])

  return (
    <div className="flex min-h-dvh">
      {/* Tastaturbrugere skal kunne springe navigationen over. */}
      <a
        href="#hovedindhold"
        className="sr-only-focusable fixed left-4 top-4 z-50 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white"
      >
        Gå til hovedindhold
      </a>

      {/* Skallen udskrives ikke. En rapport på papir skal være rapporten —
          ikke et skærmbillede af appen med en menu i venstre side. Reglerne
          står samlet i `index.css` under @media print. */}
      <aside
        data-print-hide
        className={cn(
          'hidden shrink-0 transition-[width] duration-[var(--duration-panel)] ease-[var(--ease-out-soft)] lg:block',
          collapsed ? 'w-[var(--spacing-sidebar-collapsed)]' : 'w-[var(--spacing-sidebar)]',
        )}
      >
        <div
          className={cn(
            'fixed inset-y-0 left-0 transition-[width] duration-[var(--duration-panel)] ease-[var(--ease-out-soft)]',
            collapsed ? 'w-[var(--spacing-sidebar-collapsed)]' : 'w-[var(--spacing-sidebar)]',
          )}
        >
          <Sidebar />
        </div>
      </aside>

      {/* Mobil: sidebaren bliver til en drawer. Radix' Dialog giver os
          fokusfælde, Escape-lukning og scroll-lås. */}
      <DialogPrimitive.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-scrim backdrop-blur-sm lg:hidden" />
          <DialogPrimitive.Content
            className={cn(
              'fixed inset-y-0 left-0 z-50 w-[var(--spacing-sidebar)] lg:hidden',
              'duration-[var(--duration-panel)] ease-[var(--ease-out-soft)]',
            )}
          >
            <VisuallyHidden>
              <DialogPrimitive.Title>Navigation</DialogPrimitive.Title>
            </VisuallyHidden>
            <Sidebar forceExpanded onNavigate={() => setMobileNavOpen(false)} />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <div className="flex min-w-0 flex-1 flex-col">
        <div data-print-hide>
          <Topbar breadcrumbs={breadcrumbs} />
        </div>

        <main
          id="hovedindhold"
          tabIndex={-1}
          className="flex-1 px-3 py-4 pb-24 sm:px-5 lg:px-8 lg:py-6 lg:pb-8"
        >
          <Outlet />
        </main>

        <div data-print-hide>
          <MobileBottomNavigation />
        </div>
      </div>

      {/* Ligger inde i router-konteksten, fordi paletten navigerer. */}
      <CommandPalette />
    </div>
  )
}

/**
 * Bundnavigation på mobil med de fire vigtigste områder.
 * Touch-mål er mindst 44 × 44 px, jf. WCAG 2.2 Target Size.
 */
function MobileBottomNavigation() {
  return (
    <nav
      aria-label="Hurtig navigation"
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 lg:hidden',
        'border-t border-line-subtle bg-app-950/92 backdrop-blur-xl',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <ul className="flex">
        {MOBILE_NAVIGATION.map((item) => {
          const Icon = item.icon

          return (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                aria-disabled={item.comingSoon || undefined}
                onClickCapture={(event) => {
                  if (item.comingSoon) event.preventDefault()
                }}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-[3.25rem] flex-col items-center justify-center gap-0.5 px-1 py-2',
                    'text-[11px] font-medium transition-colors',
                    item.comingSoon
                      ? 'text-text-muted/60'
                      : isActive
                        ? 'text-brand-300'
                        : 'text-text-muted',
                  )
                }
              >
                <Icon className="size-5" aria-hidden="true" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
